import { CSSProperties, PropsWithChildren, forwardRef, useCallback, useRef } from 'react';
import './dialog.scss';

type UseDialogParams = { onOpen?: () => void; onClose?: () => void };
export type UseDialogReturnType = {
  ref: React.RefObject<HTMLDialogElement>;
  openDialog: () => void;
  closeDialog: () => void;
};

export const useDialog = ({ onOpen, onClose }: UseDialogParams = {}): UseDialogReturnType => {
  const ref = useRef<HTMLDialogElement>(null);

  const openDialog = useCallback(() => {
    // If already open, do nothing
    if (ref.current?.open) {
      return;
    }
    ref.current?.showModal();
    if (onOpen) {
      onOpen();
    }
  }, [onOpen]);

  const closeDialog = useCallback(() => {
    // If not already open, don't do anything. This avoids weird animation race conditions
    if (!ref.current?.open) {
      return;
    }
    ref.current?.classList.add('close');
    ref.current?.addEventListener(
      'animationend',
      () => {
        ref.current?.classList.remove('close');
        ref.current?.close();
        if (onClose) {
          onClose();
        }
      },
      { once: true }
    );
  }, [onClose]);

  return { ref, openDialog, closeDialog };
};

const Dialog = forwardRef<
  HTMLDialogElement,
  PropsWithChildren & {
    closeDialog: () => void;
    id?: string;
    width?: string;
    ['data-cy']?: string;
    style?: CSSProperties;
  }
>(function ({ children, closeDialog, id, width, style, ...props }, ref) {
  const onClick = useCallback(
    (e: React.MouseEvent<HTMLDialogElement, MouseEvent>) => {
      // Only the <dialog> element itself receives backdrop clicks; clicks on the
      // content bubble up from child nodes. Ignoring those prevents a stray click
      // from the opening gesture (or a child's keyboard activation) from closing it.
      if (e.target !== e.currentTarget) {
        return;
      }
      // Keyboard/synthetic clicks report (0,0); never treat those as a backdrop click.
      if (e.clientX === 0 && e.clientY === 0) {
        return;
      }
      const rect = e.currentTarget.getBoundingClientRect();
      const isInDialog =
        e.clientY >= rect.top && e.clientY <= rect.bottom && e.clientX >= rect.left && e.clientX <= rect.right;
      if (!isInDialog) {
        closeDialog();
      }
    },
    [closeDialog]
  );

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDialogElement>) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        closeDialog();
      }
    },
    [closeDialog]
  );

  return (
    <dialog
      style={{ width, ...style }}
      id={id}
      ref={ref}
      onClick={onClick}
      onKeyDown={onKeyDown}
      data-cy={props['data-cy']}
    >
      {children}
    </dialog>
  );
});

export default Dialog;
